import type { RunExecutionRefusalBody } from "common";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The run page's launch refusal state (3G-3a spec 5.3 and 5.4).
 *
 * Three pieces of state with three lifetimes, kept apart on purpose (D12):
 * - `panelView`: what the user sees above Launch. Until dismissed, replaced, or the view changes run or attempt.
 * - `pendingSelection`: which environment an unsaved fallback switch means. Until a run details response shows it
 *   persisted, the user picks another environment themselves, or the view changes. Dismiss does NOT clear it.
 * - launch identity: a `LaunchRef` captured when a launch is submitted, carried with the initiate mutation and
 *   handed back with the refusal. Nothing is reconstructed from form values when the response arrives.
 *
 * The picker filter is component state here, never a form field (D11): `handleSaveRun` sweeps unknown form keys
 * into the payload.
 */

export type LaunchRef = {
  /** Monotonic, one per logical launch. The 389 auto retry keeps the sequence of the launch it retries (D14). */
  sequence: number;
  runId: string;
  /** "" for the current attempt. Attempts share a run id, so a run id check alone cannot notice an attempt change. */
  attemptId: string;
  /** The environment the save persisted for this launch; informational, the refusal is about it. */
  runEnvironmentId: number | null;
  /** Bumped on every user environment edit, so changing away and back is still a change. */
  environmentGeneration: number;
};

export type LaunchRefusalPanelView =
  | {
      kind: "refused";
      refusal: RunExecutionRefusalBody;
      /** The environment the refused launch submitted (D10), so the panel can link to it (agent-4-2). */
      runEnvironmentId?: number | null;
    }
  | { kind: "switched"; environmentName: string }
  | null;

export type EnvironmentPickerFilter = { transport: "ssh" } | null;

export type LaunchRefusalFocusTarget =
  | "environment-selector"
  | "panel-confirmation"
  | "picker-opener";

/** The run environment selector's text input (react-select `inputId`), rendered by overview/settings/settings.tsx. */
export const RUN_ENVIRONMENT_SELECT_INPUT_ID = "run-environment-select-input";
/** The panel's confirmation line after a switch, focused after a choice in the picker (spec 5.3). */
export const LAUNCH_REFUSAL_CONFIRMATION_ID = "launch-refusal-confirmation";
/** The panel's "Choose an SSH environment" control, focused again when the picker is cancelled. */
export const LAUNCH_REFUSAL_PICKER_BUTTON_ID =
  "launch-refusal-choose-ssh-environment";

const FOCUS_TARGET_IDS: Record<LaunchRefusalFocusTarget, string> = {
  "environment-selector": RUN_ENVIRONMENT_SELECT_INPUT_ID,
  "panel-confirmation": LAUNCH_REFUSAL_CONFIRMATION_ID,
  "picker-opener": LAUNCH_REFUSAL_PICKER_BUTTON_ID,
};

type View = { runId: string; attemptId: string };

export const useLaunchRefusal = (view: View) => {
  const [panelView, setPanelView] = useState<LaunchRefusalPanelView>(null);
  const [pendingSelection, setPendingSelection] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [pickerFilter, setPickerFilter] =
    useState<EnvironmentPickerFilter>(null);
  const [focusRequest, setFocusRequest] = useState<{
    target: LaunchRefusalFocusTarget;
    seq: number;
  } | null>(null);
  const sequenceRef = useRef(0);
  const generationRef = useRef(0);
  const viewRef = useRef(view);
  viewRef.current = view;

  // A change of run or attempt: clear the three together and bump the sequence, so any response from the
  // previous view is rejected on arrival (spec 5.4, "Clearing").
  const previousViewRef = useRef(view);
  useEffect(() => {
    const previous = previousViewRef.current;
    if (
      previous.runId !== view.runId ||
      previous.attemptId !== view.attemptId
    ) {
      sequenceRef.current += 1;
      setPanelView(null);
      setPendingSelection(null);
      setPickerFilter(null);
    }
    previousViewRef.current = view;
  }, [view.runId, view.attemptId]);

  // Focus moves after the render that shows the target: the Run Settings tab is hidden with a class until the
  // page switches to it, and the confirmation line exists only once the panel shows it.
  useEffect(() => {
    if (!focusRequest) return;
    document.getElementById(FOCUS_TARGET_IDS[focusRequest.target])?.focus();
  }, [focusRequest]);

  const requestFocus = useCallback((target: LaunchRefusalFocusTarget) => {
    setFocusRequest(prev => ({ target, seq: (prev?.seq ?? 0) + 1 }));
  }, []);

  const beginLaunch = useCallback(
    (runEnvironmentId: number | null): LaunchRef => {
      sequenceRef.current += 1;
      // A new launch hides any earlier refusal and closes an open SSH picker, so no SSH-only menu
      // stays open behind the launch.
      setPanelView(null);
      setPickerFilter(null);
      return {
        sequence: sequenceRef.current,
        runId: viewRef.current.runId,
        attemptId: viewRef.current.attemptId,
        runEnvironmentId,
        environmentGeneration: generationRef.current,
      };
    },
    []
  );

  const acceptRefusal = useCallback(
    (
      refusal: RunExecutionRefusalBody,
      launchRef: LaunchRef | undefined
    ): boolean => {
      if (!launchRef) return false;
      const current = viewRef.current;
      if (launchRef.sequence !== sequenceRef.current) return false;
      if (
        launchRef.runId !== current.runId ||
        launchRef.attemptId !== current.attemptId
      ) {
        return false;
      }
      if (launchRef.environmentGeneration !== generationRef.current) {
        return false;
      }
      setPanelView({
        kind: "refused",
        refusal,
        runEnvironmentId: launchRef.runEnvironmentId,
      });
      return true;
    },
    []
  );

  const noteLaunchSucceeded = useCallback(() => setPanelView(null), []);

  const switchEnvironment = useCallback(
    (
      environment: { id: number; name: string },
      focus: LaunchRefusalFocusTarget
    ) => {
      generationRef.current += 1;
      setPendingSelection(environment);
      setPickerFilter(null);
      setPanelView({ kind: "switched", environmentName: environment.name });
      requestFocus(focus);
    },
    [requestFocus]
  );

  const noteUserEnvironmentChange = useCallback(() => {
    generationRef.current += 1;
    setPendingSelection(null);
    setPickerFilter(null);
    setPanelView(null);
  }, []);

  const openPicker = useCallback(() => {
    setPickerFilter({ transport: "ssh" });
    requestFocus("environment-selector");
  }, [requestFocus]);

  const cancelPicker = useCallback(() => {
    setPickerFilter(null);
    requestFocus("picker-opener");
  }, [requestFocus]);

  // Dismiss hides, nothing more (review finding 1): the pending selection keeps guarding the refetch.
  const dismiss = useCallback(() => {
    setPanelView(null);
    setPickerFilter(null);
  }, []);

  // The value is persisted once a run details response carries it; until then the guard stays up.
  const reconcileWithFetched = useCallback(
    (fetchedSelectedId: number | null) => {
      setPendingSelection(prev =>
        prev !== null && prev.id === fetchedSelectedId ? null : prev
      );
    },
    []
  );

  return {
    panelView,
    pendingSelection,
    pickerFilter,
    beginLaunch,
    acceptRefusal,
    noteLaunchSucceeded,
    switchEnvironment,
    noteUserEnvironmentChange,
    openPicker,
    cancelPicker,
    dismiss,
    reconcileWithFetched,
  };
};
